import type { LintDiagnostic } from '../../types/lint.js';
import type { CharterFrontmatter } from '../../types/charter.js';

export function runSemanticRules(file: string, charter: CharterFrontmatter): LintDiagnostic[] {
  const diagnostics: LintDiagnostic[] = [];

  // High-risk agents in prod should use hash-only or redacted logging
  if (charter.risk_tier === 'High' && charter.environment === 'prod' && charter.logging_mode === 'full-local') {
    diagnostics.push({
      file,
      code: 'CHARTER.SEMANTIC.PROD_FULL_LOGGING',
      path: 'logging_mode',
      severity: 'warning',
      message: 'High-risk production agents should not use full-local logging (consider hash-only or redacted)',
    });
  }

  // Budget enforcement should be block for prod (only if budget is present)
  if (charter.budget) {
    if (charter.environment === 'prod' && charter.budget.enforcement && charter.budget.enforcement !== 'block') {
      diagnostics.push({
        file,
        code: 'CHARTER.SEMANTIC.PROD_BUDGET_ENFORCEMENT',
        path: 'budget.enforcement',
        severity: 'warning',
        message: `Production agents should use "block" budget enforcement, not "${charter.budget.enforcement}"`,
      });
    }

    // Check that budget has proper type-specific limits
    if (charter.budget.type === 'tokens' && !charter.budget.limit_tokens) {
      diagnostics.push({
        file,
        code: 'CHARTER.SEMANTIC.BUDGET_TOKENS_MISSING',
        path: 'budget.limit_tokens',
        severity: 'error',
        message: 'Budget type is "tokens" but limit_tokens is not set',
      });
    }

    if (charter.budget.type === 'dollars' && !charter.budget.limit_dollars) {
      diagnostics.push({
        file,
        code: 'CHARTER.SEMANTIC.BUDGET_DOLLARS_MISSING',
        path: 'budget.limit_dollars',
        severity: 'error',
        message: 'Budget type is "dollars" but limit_dollars is not set',
      });
    }

    if (charter.budget.type === 'both') {
      if (!charter.budget.limit_tokens) {
        diagnostics.push({
          file,
          code: 'CHARTER.SEMANTIC.BUDGET_TOKENS_MISSING',
          path: 'budget.limit_tokens',
          severity: 'error',
          message: 'Budget type is "both" but limit_tokens is not set',
        });
      }
      if (!charter.budget.limit_dollars) {
        diagnostics.push({
          file,
          code: 'CHARTER.SEMANTIC.BUDGET_DOLLARS_MISSING',
          path: 'budget.limit_dollars',
          severity: 'error',
          message: 'Budget type is "both" but limit_dollars is not set',
        });
      }
    }
  }

  // ENG-6707 Phase 2: tools.skills.write_team / publish / write_organization are
  // deprecated and ignored. Agent-driven skill authoring is governed by the
  // SkillSpector scanner gate (clean scan auto-publishes shared skills, findings
  // hold them for review), and the only remaining knob is the default-open
  // tools.skills.shared_authoring kill switch. Nudge operators carrying the old
  // flags to migrate so the charter doesn't imply a gate that no longer exists.
  const skillsTools = charter.tools?.skills as
    | { write_team?: unknown; publish?: unknown; write_organization?: unknown }
    | undefined;
  if (
    skillsTools &&
    (skillsTools.write_team !== undefined ||
      skillsTools.publish !== undefined ||
      skillsTools.write_organization !== undefined)
  ) {
    diagnostics.push({
      file,
      code: 'CHARTER.SEMANTIC.SKILL_FLAGS_DEPRECATED',
      path: 'tools.skills',
      severity: 'info',
      message:
        'tools.skills.write_team / publish / write_organization are deprecated and ignored (ENG-6707). Agent-driven skill authoring is governed by the SkillSpector scan; set tools.skills.shared_authoring: false to revoke shared-scope authoring for a compromised agent.',
    });
  }

  return diagnostics;
}
